window.ThemeCart = window.ThemeCart || {};

class ThemeCartDiscountSplitGrouping {
  static actionsInFlight = new WeakSet();

  static parseMembers(cartItem) {
    const keys = (cartItem?.dataset?.lineKeys || "")
      .split(",")
      .map((key) => key.trim())
      .filter(Boolean);
    if (keys.length < 2) return null;

    const quantitiesByKey = new Map();
    cartItem
      .closest("form")
      ?.querySelectorAll('input[type="hidden"][name="updates[]"][data-group-member][data-line-id]')
      .forEach((input) => {
        quantitiesByKey.set(
          String(input.dataset.lineId),
          Math.max(Number(input.value) || 0, 0)
        );
      });

    const members = keys
      .map((key) => {
        if (!quantitiesByKey.has(key)) return null;
        return { key, quantity: quantitiesByKey.get(key) };
      })
      .filter(Boolean);

    return members.length === keys.length ? members : null;
  }

  static updatesForTargetQuantity(members, targetQuantity, maxPerLine = 300) {
    const updates = {};
    const currentTotal = members.reduce(
      (sum, member) => sum + member.quantity,
      0
    );
    let delta = targetQuantity - currentTotal;

    if (delta > 0) {
      members.forEach((member) => {
        if (delta <= 0) return;
        const capacity = Math.max(maxPerLine - member.quantity, 0);
        const added = Math.min(capacity, delta);
        if (added > 0) {
          updates[member.key] = member.quantity + added;
          delta -= added;
        }
      });
    } else if (delta < 0) {
      let remainingToRemove = Math.abs(delta);
      members.forEach((member) => {
        if (remainingToRemove <= 0) return;
        const removed = Math.min(member.quantity, remainingToRemove);
        if (removed > 0) {
          updates[member.key] = member.quantity - removed;
          remainingToRemove -= removed;
        }
      });
    }

    return updates;
  }

  static async renderCart(cartDrawer, fromMainCart) {
    await cartDrawer.renderCart(false, false, false, null, false, true);
    if (fromMainCart) await cartDrawer.refreshMainCartPageSection();
  }

  static async handleAction(cartDrawer, event, target, cartItem) {
    const members = ThemeCartDiscountSplitGrouping.parseMembers(cartItem);
    if (!members) return false;

    event.preventDefault();
    event.stopPropagation();
    const fromMainCart = Boolean(target.closest("main-cart"));
    if (ThemeCartDiscountSplitGrouping.actionsInFlight.has(cartDrawer)) return true;
    ThemeCartDiscountSplitGrouping.actionsInFlight.add(cartDrawer);

    const actionController = new AbortController();
    cartDrawer._quantityUpdateController?.abort?.();
    cartDrawer._quantityUpdateController = actionController;
    cartItem
      .querySelectorAll("button, input.cart-item__quantity")
      .forEach((control) => control.setAttribute("disabled", "disabled"));
    cartItem.querySelector(".itemLoader")?.classList.add("is-visible");

    try {
      const currentTotal = members.reduce(
        (sum, member) => sum + member.quantity,
        0
      );
      const maxPerLine = Number(window.theme?.cartLineQuantityMax) || 300;
      const maxGroupedQuantity = maxPerLine * members.length;
      const action = target.dataset.action;
      let targetQuantity;

      if (action === "remove-item") {
        targetQuantity = 0;
      } else if (target.tagName === "INPUT") {
        targetQuantity = Math.min(
          Math.max(parseInt(target.value, 10) || 0, 0),
          maxGroupedQuantity
        );
      } else {
        targetQuantity = Math.min(
          Math.max(parseInt(target.dataset.quantity, 10) || 0, 0),
          maxGroupedQuantity
        );
      }

      if (targetQuantity === currentTotal) return true;

      const updates =
        targetQuantity === 0
          ? Object.fromEntries(members.map((member) => [member.key, 0]))
          : ThemeCartDiscountSplitGrouping.updatesForTargetQuantity(
              members,
              targetQuantity,
              maxPerLine
            );
      const response = await window.ThemeCart.CartApi.update(
        { updates },
        actionController.signal
      );

      if (!response.ok) {
        let message = "";
        try {
          const payload = await response.json();
          if (typeof parseCartChangeErrorBody === "function") {
            message = parseCartChangeErrorBody(payload);
          }
        } catch (error) {
          /* use the fallback message */
        }
        message ||= window.cartStrings?.outOfStock || window.cartStrings?.error || "";
        await ThemeCartDiscountSplitGrouping.renderCart(
          cartDrawer,
          fromMainCart
        );
        if (typeof showCartItemQuantityFeedback === "function") {
          const renderedRow =
            typeof findCartLineRowByLineId === "function"
              ? findCartLineRowByLineId(members[0].key, target)
              : cartItem;
          showCartItemQuantityFeedback(renderedRow, message);
        }
        return true;
      }

      await ThemeCartDiscountSplitGrouping.renderCart(
        cartDrawer,
        fromMainCart
      );
      return true;
    } catch (error) {
      if (error?.name !== "AbortError") {
        cartDrawer._handleCartRequestFailure(error);
        await ThemeCartDiscountSplitGrouping.renderCart(
          cartDrawer,
          fromMainCart
        );
      }
      return true;
    } finally {
      if (cartDrawer._quantityUpdateController === actionController) {
        cartDrawer._quantityUpdateController = null;
      }
      ThemeCartDiscountSplitGrouping.actionsInFlight.delete(cartDrawer);
      if (cartItem.isConnected) {
        cartItem
          .querySelectorAll("button, input.cart-item__quantity")
          .forEach((control) => control.removeAttribute("disabled"));
        cartItem.querySelector(".itemLoader")?.classList.remove("is-visible");
      }
    }
  }
}

window.ThemeCart.DiscountSplitGrouping = ThemeCartDiscountSplitGrouping;
